import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import type { ISecondaryNavigation, TPrimaryNavigationItem } from 'components/organisms/header-v2/entities/types';
import { HeaderV2Component } from 'components/organisms/header-v2/header-v2.component';
import { HeaderV2Service } from 'components/organisms/header-v2/service/header-v2.service';
import { HeaderViewService } from 'components/organisms/header-v2/service/header-view.service';
import { StickyDirective } from 'src/directives/sticky/sticky.directive';
import type { ILocale } from 'src/providers/l10n/types';
import { EnvironmentService } from 'src/services/environment/environment.service';
import { FormRegParamsParserService } from 'src/services/form-reg-params-parser/form-reg-params-parser.serivce';
import { LocaleService } from 'src/services/locale/locale.service';
import { UrlLocaliseService } from 'src/services/url-localise/url-localise.service';
import { getTrialFormDefaultStrings } from 'src/shared/constants/strings/trial-form-strings';
import type { IHeader, IRegistrationForm } from 'src/shared/types/header';
import type { ILink, TLayoutType } from 'src/shared/types/link';
import type { ILocalizableLink } from 'src/shared/types/localizable-link';
import type { IRegistrationSettings } from 'src/shared/types/registration';
import { EStickySlot } from 'src/shared/types/sticky';
import { PRODUCT_TYPE } from 'src/tokens/product-type/product-type';

import { LayoutDataService } from '../services/layout-data.service';
import { LayoutLocalizationService } from '../services/layout-localization.service';
import { getHeaderRebrandedSecondaryNavData, transformHeaderData } from './entities/const';

@Component({
    selector: 'app-rebranded-header',
    templateUrl: 'rebranded-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [HeaderV2Component, AsyncPipe, StickyDirective],
})
export class RebrandedHeaderComponent implements OnInit {
    readonly headerViewService = inject(HeaderViewService);
    readonly headerService = inject(HeaderV2Service);
    readonly formRegParamsParserService = inject(FormRegParamsParserService);
    private readonly localeService = inject(LocaleService);
    private readonly layoutDataService = inject(LayoutDataService);
    private readonly layoutLocalizationService = inject(LayoutLocalizationService);
    private readonly translateService = inject(TranslateService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly productType = inject(PRODUCT_TYPE);
    readonly urlLocaliseService = inject(UrlLocaliseService);
    public primaryNavigation: TPrimaryNavigationItem[];
    public secondaryNavigation: ISecondaryNavigation;
    public currentLocale: ILocale;
    protected headerData: IHeader;
    protected readonly headerRebrandedSecondaryNavData = getHeaderRebrandedSecondaryNavData(
        this.translateService.instant,
        this.environmentService.environment.loginPageLink
    );
    protected readonly trialFormDefaultStrings = getTrialFormDefaultStrings();
    private readonly headerTrialFormSendingData: IRegistrationSettings = {
        plan: 'ENT_2021_ADVANCED',
        formId: 'sticky_header',
    };
    public readonly stickySlot = EStickySlot.Header;

    constructor() {
        this.setHeaderData();
    }

    private setHeaderData(): void {
        this.headerData = transformHeaderData(this.layoutDataService.getHeaderData());
    }

    public ngOnInit(): void {
        this.primaryNavigation = this.getLocalizedHeaderNavigation();
        const ctaFields = this.headerViewService?.registrationForm?.ctaFields || ({} as IRegistrationForm['ctaFields']);

        this.secondaryNavigation = this.getSecondaryNavigation(ctaFields);
        this.currentLocale = this.localeService.getCurrentLocale();
    }

    private getSecondaryNavigation(ctaFields: IRegistrationForm['ctaFields']): ISecondaryNavigation {
        if (this.productType === 'klaxoon') {
            return this.getKlaxoonSecondaryNavigation();
        }
        return this.getDefaultSecondaryNavigation(ctaFields);
    }

    private getDefaultSecondaryNavigation(ctaFields: IRegistrationForm['ctaFields']): ISecondaryNavigation {
        return {
            contactSales: this.localizeLinks([this.headerRebrandedSecondaryNavData.contactSales])[0],
            login: {
                ...this.headerRebrandedSecondaryNavData.login,
                ...{ title: this.translateService.instant(this.headerRebrandedSecondaryNavData.login.title) },
            },
            trialForm: this.getTrialFormData(this.headerViewService?.registrationForm),
            popupTitle:
                ctaFields.popupTitle || this.translateService.instant(this.headerRebrandedSecondaryNavData.popupTitle),
            popupSubtitle:
                ctaFields.popupSubtitle ||
                this.translateService.instant(this.headerRebrandedSecondaryNavData.popupSubtitle),
            mobileButtonSendText:
                this.headerViewService?.registrationForm?.formOptions?.mobileButtonSendText ||
                this.translateService.instant(this.headerRebrandedSecondaryNavData.mobileButtonSendText),
        };
    }

    private getKlaxoonSecondaryNavigation(): ISecondaryNavigation {
        return {
            login: {
                ...this.headerRebrandedSecondaryNavData.login,
                ...{ title: this.translateService.instant(this.headerRebrandedSecondaryNavData.login.title) },
                url: `${this.headerRebrandedSecondaryNavData.login.url}?lang=${this.localeService.getCurrentLocaleSlug()}`,
            },
            bookDemo: this.localizeLinks([this.headerRebrandedSecondaryNavData.bookDemo])[0],
        };
    }

    private getTrialFormData(registrationForm: IRegistrationForm | null): ISecondaryNavigation['trialForm'] {
        const formOptions = registrationForm?.formOptions || ({} as IRegistrationForm['formOptions']);

        return {
            buttonSendText:
                formOptions.buttonSendText ||
                this.translateService.instant(this.headerRebrandedSecondaryNavData.trialFormStrings.buttonSendText),
            inputPlaceholderText:
                formOptions.inputPlaceholder ||
                this.translateService.instant(
                    this.headerRebrandedSecondaryNavData.trialFormStrings.inputPlaceholderText
                ),
            inputLabelText:
                formOptions.inputLabel ||
                this.translateService.instant(this.headerRebrandedSecondaryNavData.trialFormStrings.inputLabelText),
            trialFormSendingData: formOptions.params
                ? this.formRegParamsParserService.prepareRegistrationSettings(formOptions.params)
                : this.headerTrialFormSendingData,
            errorList: Object.keys(this.trialFormDefaultStrings.errorList).reduce((acc, key) => {
                acc[key] = this.translateService.instant(this.trialFormDefaultStrings.errorList[key]);
                return acc;
            }, {}) as any,
            environment: this.environmentService.environment.origin,
            loginRedirectUrl: this.environmentService.environment.loginPageLink,
            hasGoogleAuth: formOptions?.params?.hasGoogleAuth,
            popupBottomImageUrl: formOptions?.params?.popupBottomImageUrl,
            googleButtonText: formOptions?.params?.customGoogleLinkText
                ? {
                      business: formOptions?.params?.customGoogleLinkText,
                      nonBusiness: formOptions?.params?.customGoogleLinkText,
                  }
                : {
                      business: this.translateService.instant(
                          this.headerRebrandedSecondaryNavData.trialFormStrings.googleButtonText.business
                      ),
                      nonBusiness: this.translateService.instant(
                          this.headerRebrandedSecondaryNavData.trialFormStrings.googleButtonText.nonBusiness
                      ),
                  },
            emailValidation: formOptions?.params?.emailValidation,
        };
    }

    private getLocalizedHeaderNavigation(): TPrimaryNavigationItem[] {
        return this.headerData.headerMenu.mainNavigation.reduce((acc: TPrimaryNavigationItem[], navItem) => {
            if (
                this.layoutLocalizationService.checkIsItemHiddenForCurrentLocale(navItem) === false ||
                !Object.prototype.hasOwnProperty.call(navItem, 'displayOnly')
            ) {
                if ('submenu' in navItem) {
                    // handle ILinksGroup
                    if (navItem.submenu.length === 1) {
                        acc.push({
                            key: navItem.key,
                            title: this.translateService.instant(navItem.title),
                            links: this.localizeLinks(navItem.submenu[0].items),
                            layout_type: (navItem.layout_type as TLayoutType) || '',
                            promo: navItem.promo,
                            cta_list: navItem.cta_list,
                            label: navItem?.label,
                        });
                    }

                    // handle ILinksSubgroups
                    if (navItem.submenu.length > 1) {
                        acc.push({
                            title: this.translateService.instant(navItem.title),
                            key: navItem.key,
                            cta_list: navItem.cta_list,
                            subgroups: navItem.submenu.reduce((submenuAcc, submenu) => {
                                if (
                                    this.layoutLocalizationService.checkIsItemHiddenForCurrentLocale(submenu) === false
                                ) {
                                    submenuAcc.push({
                                        key: '',
                                        title: this.translateService.instant(submenu.title),
                                        links: this.localizeLinks(submenu.items),
                                        description: submenu?.['description'],
                                        label: submenu?.label,
                                    });
                                }

                                return submenuAcc;
                            }, []),
                            layout_type: (navItem.layout_type as TLayoutType) || '',
                            promo: navItem.promo,
                            promos: navItem.promos ?? [],
                            label: navItem?.label,
                        });
                    }
                    // handle ILink
                } else {
                    const url = this.layoutLocalizationService.getLocalizableUrl(navItem);

                    if (url) {
                        acc.push({
                            key: navItem.key,
                            title: this.translateService.instant(navItem.title),
                            description: navItem.description,
                            is_highlighted: navItem.is_highlighted ? navItem.is_highlighted : false,
                            image: navItem.image ? navItem.image : '',
                            animatedIconCode: navItem.animatedIconCode,
                            url,
                            cta_list: navItem.cta_list,
                            label: navItem?.label,
                        });
                    }
                }
            }

            return acc;
        }, []);
    }

    private localizeLinks(links: ILocalizableLink[]): ILink[] {
        return links.reduce((linksAcc: ILink[], link) => {
            const url = this.layoutLocalizationService.getLocalizableUrl(link);

            if (url) {
                linksAcc.push({
                    key: link.key,
                    title: this.translateService.instant(link.title),
                    titleMobile: link.titleMobile ? this.translateService.instant(link.titleMobile) : '',
                    description: link.description,
                    is_highlighted: link.is_highlighted ? link.is_highlighted : false,
                    image: link.image ? link.image : '',
                    animatedIconCode: link.animatedIconCode,
                    url,
                    label: link.label,
                });
            }

            return linksAcc;
        }, []);
    }
}
